import React, { useEffect, useMemo, useState } from 'react';
import { Button, Checkbox, Modal, Spin, message } from 'antd';
import { useTranslation } from '../../../i18n';
import { cancelCollaborations, getCollaborationDetail, removeCollaborationCreators } from '../../../store/request';
import type { TargetCollaborationCreator } from '../../../types/collaboration';
import { CreatorAcceptTag } from './CreatorAcceptTag';

type CleanupMode = 'clear_unaccepted' | 'cancel_plan';

type CleanupInviteModalProps = {
  open: boolean;
  shopId?: string | null;
  collaborationIds: number[];
  onClose: () => void;
  onCancelled?: () => void;
};

function methodCardClass(active: boolean) {
  return [
    'w-full cursor-pointer rounded-lg border-2 p-3 text-left transition-colors',
    active ? 'border-[#596ae8] bg-[#596ae8]/[0.04]' : 'border-[#eef0f4] bg-white hover:border-[#596ae8]/30'
  ].join(' ');
}

function creatorName(creator: TargetCollaborationCreator) {
  return (
    creator.nickname?.trim() ||
    creator.platformNickname?.trim() ||
    creator.creatorName?.trim() ||
    creator.username?.trim() ||
    creator.platformUsername?.trim() ||
    creator.creatorOpenId?.trim() ||
    '—'
  );
}

function creatorMeta(creator: TargetCollaborationCreator) {
  const handle = (creator.username?.trim() || creator.platformUsername?.trim() || '').replace(/^@/, '');
  const region = creator.selectionRegion?.trim() || '';
  return [handle ? `@${handle}` : '', region].filter(Boolean).join(' · ');
}

export function CleanupInviteModal({ open, shopId, collaborationIds, onClose, onCancelled }: CleanupInviteModalProps) {
  const { t } = useTranslation();
  const [mode, setMode] = useState<CleanupMode>('clear_unaccepted');
  const [whitelistIds, setWhitelistIds] = useState<string[]>([]);
  const [creators, setCreators] = useState<TargetCollaborationCreator[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const currentShopId = shopId?.trim() || '';

  const cleanupOptions = useMemo(
    () => [
      {
        key: 'clear_unaccepted' as const,
        title: t('invitation.mode_clear_title'),
        description: t('invitation.mode_clear_desc')
      },
      {
        key: 'cancel_plan' as const,
        title: t('invitation.mode_cancel_title'),
        description: t('invitation.mode_cancel_desc')
      }
    ],
    [t]
  );

  useEffect(() => {
    if (!open) return;
    setMode('clear_unaccepted');
    setWhitelistIds([]);
    setSubmitting(false);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const inviteIds = Array.from(new Set(collaborationIds.filter((id) => Number.isInteger(id))));
    if (!currentShopId || inviteIds.length === 0) {
      setCreators([]);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setCreators([]);
    Promise.all(inviteIds.map((id) => getCollaborationDetail(id, { shopId: currentShopId })))
      .then((results) => {
        if (cancelled) return;
        const failed = results.find((res) => res.code !== 200);
        if (failed) {
          if (failed.code !== -1) message.error(failed.message || t('invitation.detail_load_failed'));
          setCreators([]);
          return;
        }
        const byId = new Map<string, TargetCollaborationCreator>();
        results.forEach((res) => {
          const list = res.content?.creators;
          if (!Array.isArray(list)) return;
          list.forEach((creator) => {
            const id = creator.creatorOpenId?.trim();
            if (!id || byId.has(id)) return;
            byId.set(id, creator);
          });
        });
        setCreators(Array.from(byId.values()));
      })
      .catch(() => {
        if (!cancelled) {
          message.error(t('invitation.detail_load_failed'));
          setCreators([]);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, currentShopId, collaborationIds, t]);

  const toggleWhitelist = (id: string, checked: boolean) => {
    setWhitelistIds((prev) => (checked ? (prev.includes(id) ? prev : [...prev, id]) : prev.filter((item) => item !== id)));
  };

  const removeUnaccepted = async () => {
    if (submitting || loading) return;
    if (!currentShopId) {
      message.warning(t('invitation.invite_shop_required'));
      return;
    }
    const inviteIds = Array.from(new Set(collaborationIds.filter((id) => Number.isInteger(id))));
    if (inviteIds.length === 0) {
      message.warning(t('invitation.invite_pick_required'));
      return;
    }
    if (inviteIds.length > 10) {
      message.warning(t('invitation.invite_limit'));
      return;
    }
    const creatorOpenIds = Array.from(new Set(whitelistIds.map((id) => id.trim()).filter(Boolean)));
    setSubmitting(true);
    try {
      const res = await removeCollaborationCreators({
        shopId: currentShopId,
        collaborationIds: inviteIds,
        creatorOpenIds
      });
      if (res.code !== 200) {
        if (res.code !== -1) message.error(res.message || t('invitation.remove_talent_failed'));
        return;
      }
      const result = res.content;
      const noneRemoved =
        result != null &&
        (result.successCount ?? 0) === 0 &&
        (result.partialSuccessCount ?? 0) === 0 &&
        (result.failureCount ?? 0) > 0;
      if (noneRemoved) {
        message.warning(res.message || t('invitation.remove_talent_failed'));
        return;
      }
      message.success(res.message || t('invitation.remove_talent_success'));
      onCancelled?.();
      onClose();
    } catch {
      message.error(t('invitation.remove_talent_failed'));
    } finally {
      setSubmitting(false);
    }
  };

  const selectedInviteIds = () => {
    if (!currentShopId) {
      message.warning(t('invitation.invite_shop_required'));
      return null;
    }
    const inviteIds = Array.from(new Set(collaborationIds.filter((id) => Number.isInteger(id))));
    if (inviteIds.length === 0) {
      message.warning(t('invitation.invite_pick_required'));
      return null;
    }
    if (inviteIds.length > 10) {
      message.warning(t('invitation.invite_limit'));
      return null;
    }
    return inviteIds;
  };

  const cancelPlans = async () => {
    if (submitting) return;
    const inviteIds = selectedInviteIds();
    if (!inviteIds) return;
    setSubmitting(true);
    try {
      const res = await cancelCollaborations({
        shopId: currentShopId,
        collaborationIds: inviteIds
      });
      if (res.code !== 200) {
        if (res.code !== -1) message.error(res.message || t('invitation.cancel_plan_failed'));
        return;
      }
      const result = res.content;
      const noneCancelled =
        result != null &&
        (result.successCount ?? 0) === 0 &&
        (result.partialSuccessCount ?? 0) === 0 &&
        (result.failureCount ?? 0) > 0;
      if (noneCancelled) {
        const itemMessage = result.items?.map((item) => item.message?.trim()).find(Boolean);
        message.warning(res.message || itemMessage || t('invitation.cancel_plan_failed'));
        return;
      }
      message.success(res.message || t('invitation.cancel_plan_success'));
      onCancelled?.();
      onClose();
    } catch {
      message.error(t('invitation.cancel_plan_failed'));
    } finally {
      setSubmitting(false);
    }
  };

  const submit = () => {
    if (mode === 'cancel_plan') {
      if (!selectedInviteIds()) return;
      Modal.confirm({
        title: t('invitation.confirm_cancel_title'),
        content: t('invitation.confirm_cancel_content'),
        okText: t('invitation.confirm_cancel_ok'),
        cancelText: t('invitation.confirm_cancel_back'),
        okButtonProps: { danger: true },
        centered: true,
        onOk: () => cancelPlans()
      });
      return;
    }
    removeUnaccepted();
  };

  return (
    <Modal
      title={t('invitation.cleanup')}
      open={open}
      onCancel={onClose}
      destroyOnClose
      centered
      width={600}
      styles={{ body: { maxHeight: 'calc(100vh - 180px)', overflowY: 'auto' } }}
      closeIcon={
        <span className='cursor-pointer text-sm font-normal text-slate-500 hover:text-slate-700'>{t('common.close')}</span>
      }
      footer={
        <div className='flex justify-end gap-2'>
          <Button className='rounded-lg' onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button type='primary' className='wb-btn-primary rounded-lg' loading={submitting} onClick={submit}>
            {t('invitation.run_cleanup')}
          </Button>
        </div>
      }>
      <div className='space-y-5'>
        <div>
          <div className='mb-2 text-sm font-medium text-slate-700'>{t('invitation.choose_method')}</div>
          <div className='grid grid-cols-1 gap-3 sm:grid-cols-2'>
            {cleanupOptions.map((option) => (
              <button
                key={option.key}
                type='button'
                className={methodCardClass(mode === option.key)}
                onClick={() => setMode(option.key)}>
                <div className='text-sm font-semibold leading-snug text-slate-800'>{option.title}</div>
                <p className='mb-0 mt-2 text-xs leading-relaxed text-slate-400'>{option.description}</p>
              </button>
            ))}
          </div>
        </div>

        {mode === 'clear_unaccepted' ? (
          <div>
            <div className='mb-2 text-sm font-medium text-slate-700'>{t('invitation.whitelist_title')}</div>
            <p className='mb-2 text-xs leading-relaxed text-slate-400'>{t('invitation.remove_talent_hint')}</p>
            <p className='mb-2 text-xs leading-relaxed text-slate-400'>
              {whitelistIds.length === 0
                ? t('invitation.none_selected')
                : t('invitation.whitelist_summary', { count: whitelistIds.length })}
            </p>
            <div className='max-h-[240px] overflow-y-auto rounded-lg border border-[#eef0f4] bg-[#fafbfc] px-2 py-1'>
              {loading ? (
                <div className='flex justify-center py-6'>
                  <Spin size='small' />
                </div>
              ) : null}
              {!loading && creators.length === 0 ? (
                <p className='mb-0 px-2 py-6 text-center text-sm text-slate-400'>{t('invitation.detail_empty_talents')}</p>
              ) : null}
              {!loading
                ? creators.map((creator, index) => {
                    const id = creator.creatorOpenId?.trim() || '';
                    const name = creatorName(creator);
                    const meta = creatorMeta(creator);
                    return (
                      <label
                        key={id || `creator-${index}`}
                        className='flex cursor-pointer items-center gap-3 rounded-md px-2 py-2.5 hover:bg-white'>
                        <Checkbox
                          checked={Boolean(id) && whitelistIds.includes(id)}
                          disabled={!id}
                          onChange={(e) => toggleWhitelist(id, e.target.checked)}
                        />
                        {creator.avatarUrl?.trim() ? (
                          <img
                            src={creator.avatarUrl.trim()}
                            alt=''
                            className='size-8 shrink-0 rounded-full object-cover'
                          />
                        ) : (
                          <span className='inline-flex size-8 shrink-0 items-center justify-center rounded-full bg-[#596ae8] text-xs font-semibold text-white'>
                            {name.slice(0, 1).toUpperCase() || '?'}
                          </span>
                        )}
                        <span className='min-w-0 flex-1'>
                          <span className='flex items-center gap-2'>
                            <span className='min-w-0 truncate text-sm font-semibold text-slate-800'>{name}</span>
                            <CreatorAcceptTag label={creator.label} />
                          </span>
                          {meta ? <span className='block truncate text-xs text-slate-500'>{meta}</span> : null}
                        </span>
                      </label>
                    );
                  })
                : null}
            </div>
          </div>
        ) : null}
      </div>
    </Modal>
  );
}
